import { Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormFieldButtons } from '../form-field-buttons/form-field-buttons.component';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { FormButton } from '../form-field-button/form-field-button.component';
import { Style, TextAlign } from '@app/core/services/core-component.service';
import { BooleanInput, NumberInput, coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { debounceTime, interval, Subject, Subscription } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'tt-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true,
        },
    ],
})
export class InputComponent<TData> extends FormFieldBaseComponent implements OnInit, OnChanges, FormFieldButtons, ControlValueAccessor {
    /**
     * The text value of the form field.
     */
    @Input()
    public ttModel?: TData | null;

    /**
     * Event emitted when the text value has changed.
     */
    @Output()
    public ttModelChange = new EventEmitter<TData>();

    /**
     * Time to wait before emitting change event since last change occurred.
     */
    @Input()
    public set ttDebounceTime(value: NumberInput) {
        this._debounceTime = coerceNumberProperty(value);
    }
    public get ttDebounceTime(): number {
        return this._debounceTime;
    }
    private _debounceTime = 0;

    /**
     * The text alignment of the text inside the input field.
     */
    @Input()
    public ttTextAlign: TextAlign = 'left';

    /**
     * The type of the input field.
     */
    @Input()
    public ttType: 'date' | 'datetime-local' | 'email' | 'hidden' | 'month' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'time' | 'url' | 'week' = 'text';

    /**
     * The available input modes of a text-input. Affects which keyboard will be available on mobiles.
     */
    @Input()
    public ttInputMode: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url' = 'text';

    /**
     * Placeholder text for the input field.
     */
    @Input()
    public ttPlaceholder: string = '';

    /**
     * Placeholder value connected to the input field template.
     */
    public placeholder: string = '';

    /**
     * Whether or not the placeholder should be translated.
     *
     * @default false
     */
    @Input()
    get ttTranslatePlaceholder(): boolean {
        return this._translatePlaceholder;
    }
    set ttTranslatePlaceholder(value: BooleanInput) {
        this._translatePlaceholder = coerceBooleanProperty(value);
    }
    private _translatePlaceholder: boolean = false;

    @Input()
    public get ttMaxLength(): number {
        return this._maxLength;
    }
    public set ttMaxLength(value: NumberInput) {
        this._maxLength = coerceNumberProperty(value);
    }
    private _maxLength = 524288;

    /**
     * Whether to display a generic clear button next to the form field.
     *
     * @emits ttModelChange with an empty string.
     * @default false
     */
    @Input()
    public get ttShowClearButton(): boolean {
        return this._showClearButton;
    }
    public set ttShowClearButton(value: BooleanInput) {
        this._showClearButton = coerceBooleanProperty(value);
    }
    private _showClearButton = false;

    /**
     * Event emitted when the input field loses focus.
     */
    @Output()
    public ttOnBlur = new EventEmitter();

    @Output()
    public ttClick = new EventEmitter<MouseEvent>();

    /**
     * @default false
     */
    @Input()
    public get ttBindFocus(): boolean {
        return this._bindFocus;
    }
    public set ttBindFocus(value: BooleanInput) {
        this._bindFocus = coerceBooleanProperty(value);
    }
    private _bindFocus = false;

    /**
     * @default 500
     */
    @Input()
    public get ttBindFocusInterval(): number {
        return this._bindFocusInterval;
    }
    public set ttBindFocusInterval(value: NumberInput) {
        this._bindFocusInterval = coerceNumberProperty(value);
    }
    private _bindFocusInterval = 500;

    @Input()
    public ttButtons?: FormButton[] | undefined;

    @Input()
    public ttButtonParms?: { [key: string]: any } | undefined;

    /**
     * Reference to the input field.
     */
    @ViewChild('inputRef')
    public inputRef?: ElementRef;

    private change$ = new Subject<TData>();

    private changeSubscription?: Subscription;

    private onControlChange: (value: TData) => void = () => {};

    private onControlTouched: () => void = () => {};

    public id = {
        input: crypto.randomUUID(),
    };

    public override style: Style = {
        input: {},
    };

    public override setStyle(ttStyle = this.ttStyle) {
        this.style = this.coreComponentService.setStyle({ style: this.style, ttStyle: ttStyle ?? {}, textAlign: this.ttTextAlign, mainElement: 'input' });
    }

    public onModelChanged(event: TData) {
        this.ttModel = event;
        this.rememberSubject.next(this.ttModel);
        this.change$.next(this.ttModel);
    }

    public onFocus(_: FocusEvent) {
        this.inputRef?.nativeElement.select();
    }

    public onBlur(_: FocusEvent) {
        this.onControlTouched();
        this.ttOnBlur.emit(this.ttModel);
    }

    public clearInput() {
        if (!!this.ttModel) {
            this.ttModel = '' as TData;
            this.rememberSubject.next(this.ttModel);
            this.emitModelChange(this.ttModel);
        }
    }

    private emitModelChange(value: TData) {
        this.ttModelChange.emit(value);
        this.onControlChange(value);
    }

    public writeValue(value: TData | null | undefined): void {
        this.ttModel = value;
    }

    public registerOnChange(fn: (value: TData) => void): void {
        this.onControlChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onControlTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.ttDisabled = isDisabled;
    }

    private async getLastModelState() {
        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.ttModel = (await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue;
        }
    }

    private async setPlaceholder() {
        if (this.ttTranslatePlaceholder && this.ttPlaceholder) {
            this.placeholder = await this.translateService.translate(this.ttPlaceholder);
        } else {
            this.placeholder = this.ttPlaceholder;
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.getLastModelState();
        }

        if (!this.changeSubscription) {
            // if there is a subscription then onChanges has already created one, no need to do it again.
            this.changeSubscription = this.change$.pipe(debounceTime(this.ttDebounceTime)).subscribe({ next: (value) => this.emitModelChange(value) });
        }
    }

    focusSubscription?: Subscription;

    override async ngOnChanges(changes: SimpleChanges): Promise<void> {
        super.ngOnChanges(changes);

        if (changes['ttDebounceTime']) {
            this.ttDebounceTime = changes['ttDebounceTime'].currentValue;

            if (!!this.changeSubscription) {
                this.changeSubscription.unsubscribe();
            }

            this.changeSubscription = this.change$.pipe(debounceTime(this.ttDebounceTime)).subscribe({ next: (value) => this.emitModelChange(value) });
        }

        if (changes['ttBindFocusInterval']) {
            this.ttBindFocusInterval = changes['ttBindFocusInterval'].currentValue;
        }

        if (changes['ttBindFocus']) {
            this.ttBindFocus = changes['ttBindFocus'].currentValue;
        }

        if (changes['ttBindFocus'] || changes['ttBindFocusInterval']) {
            if (!!this.focusSubscription) {
                this.focusSubscription.unsubscribe();
            }

            if (this.ttBindFocus) {
                this.focusSubscription = interval(this.ttBindFocusInterval).subscribe({ next: (value) => this.inputRef?.nativeElement.focus() });
            }
        }

        if (changes['ttTranslatePlaceholder']) {
            this.ttTranslatePlaceholder = changes['ttTranslatePlaceholder'].currentValue;
        }

        if (!!changes['ttPlaceholder'] && (changes['ttPlaceholder'].currentValue !== changes['ttPlaceholder'].previousValue || changes['ttPlaceholder'].firstChange)) {
            this.setPlaceholder();
        }
    }
}

//           ttBindFocus: '@',
//           ttType: '@',
//           ttFunc: '&',
//       },
//       controller: ['$element', '$timeout', '$interval', 'layoutService', 'eventService', 'translateService', 'utilityService', 'ttDirectivesService', function ($element, $timeout, $interval, layoutService, eventService, translateService, us, ttDirectivesService) {
//           var vm = this;

//           var onDestroy = [];

//           vm.hideLabel = false;
//           vm.readonly = false;
//           vm.tabable = '';
//           vm.required = false;
//           vm.inputType = 'text';
//           vm.inputMode = 'text';
//           vm.maxLength = 524288;  //Webstandard default

//           vm.id = {
//               input: uuid()
//           };

//           vm.style = {
//               base: {},
//               label: {},
//               sublabel: {},
//               input: {},
//               labelAlwaysOnTop: false     // Deprecated, use ttLabelView instead. - JLR 20230622
//           };

//           vm.class = {
//               base: '',
//               label: '',
//               sublabel: '',
//               input: ''
//           };

//           vm.translations = {
//               ttLabel: '',
//               ttPlaceholder: '',
//           };

//           vm.whenReady = function () {
//               eventService.trigger('element:ready');
//           };

//           let setClasses = (labelAlwaysOnTop) => vm.class.base = ttDirectivesService.getBaseClasses({ labelAlwaysOnTop: labelAlwaysOnTop, labelView: vm.ttLabelView, hideLabel: vm.hideLabel });

//           let setStyle = (ttStyle = vm.ttStyle) => angular.copy(ttDirectivesService.setStyle({ style: vm.style, ttStyle: ttStyle, textAlign: vm.ttTextAlign, mainElement: 'input' }), vm.style);

//           var setInputMode = function (value) {
//               var element = $element.find('#' + vm.id.input);

//               element.attr('inputmode', value);
//           };

//           var isValidInputType = function (type) {
//               if (us.isStringValue(type, true) !== true) return false;

//               switch (type) {
//                   case 'button':
//                   case 'checkbox':
//                   case 'color':
//                   case 'date':
//                   case 'datetime-local':
//                   case 'email':
//                   case 'file':
//                   case 'hidden':
//                   case 'image':
//                   case 'month':
//                   case 'number':
//                   case 'password':
//                   case 'radio':
//                   case 'range':
//                   case 'reset':
//                   case 'search':
//                   case 'submit':
//                   case 'tel':
//                   case 'text':
//                   case 'time':
//                   case 'url':
//                   case 'week':
//                       return true;
//               }

//               return false;
//           };

//           var isValidInputMode = function (mode) {
//               if (us.isStringValue(mode, true) !== true) return false;

//               switch (mode) {
//                   case 'none':
//                   case 'text':
//                   case 'decimal':
//                   case 'numeric':
//                   case 'tel':
//                   case 'search':
//                   case 'email':
//                   case 'url':
//                       return true;
//               }

//               return false;
//           };

//           var inputElement;
//           var stopFocusBinding;

//           var setFocus = function () {
//               if (angular.isUndefined(inputElement)) {
//                   inputElement = $element.find('#' + vm.id.input)[0];
//               }

//               if (angular.isDefined(inputElement)) {
//                   $timeout(function () {
//                       inputElement.focus();
//                   }, 0);
//               }
//           };

//           var bindFocus = function () {
//               // return if focus already bound
//               if (angular.isDefined(stopFocusBinding)) return;

//               stopFocusBinding = $interval(setFocus, 1000);

//               $element.focus();
//           };

//           var unbindFocus = function () {
//               if (angular.isUndefined(stopFocusBinding))
//                   return;

//               $interval.cancel(stopFocusBinding);

//               stopFocusBinding = undefined;
//           };

//           vm.$onInit = function () {
//               if (angular.isDefined(vm.ttInputMode) && isValidInputMode(vm.ttInputMode) === true) {
//                   vm.inputMode = vm.ttInputMode;
//                   setInputMode(vm.inputMode);
//               }

//               if (angular.isDefined(vm.ttMaxLength)) {
//                   vm.maxLength = vm.ttMaxLength;
//               }

//               if (angular.isFunction(vm.ttFunc)) {
//                   vm.ttFunc({
//                       $value: {
//                           setFocus: setFocus,
//                           getInputElement: function () { return $element.find('#' + vm.id.input)[0]; },
//                           bindFocus: bindFocus,
//                           unbindFocus: unbindFocus
//                       }
//                   });
//               }

//               if (angular.isDefined(vm.ttInvalid) && us.toBoolean(vm.ttInvalid) === true) {
//                   vm.style.input.borderColor = 'red';
//               }

//               setStyle(vm.ttStyle);
//           }

//           var focusIsBound = false;

//           vm.$onChanges = function (changes) {
//               if (changes.ttOptions?.currentValue) {
//                   ttDirectivesService.setOptions(vm, changes);
//               }

//               if (angular.isDefined(changes.ttInvalid)) {
//                   vm.ttInvalid = us.toBoolean(changes.ttInvalid.currentValue);

//                   if (vm.ttInvalid === true) {
//                       vm.style.input.borderColor = 'red';
//                   } else {
//                       delete vm.style.input.borderColor;
//                   }
//               }

//               if (angular.isDefined(changes.ttBindFocus) && us.isStringValue(changes.ttBindFocus.currentValue)) {
//                   focusIsBound = us.toBoolean(changes.ttBindFocus.currentValue, true);

//                   if (focusIsBound === true) {
//                       bindFocus();
//                   } else {
//                       unbindFocus();
//                   }
//               }

//               if (angular.isDefined(changes.ttHideLabel)) {
//                   vm.hideLabel = us.toBoolean(changes.ttHideLabel.currentValue);

//                   setClasses();
//               }

//               if (angular.isDefined(changes.ttType) && isValidInputType(changes.ttType.currentValue) === true) {
//                   vm.inputType = changes.ttType.inputType;
//               }

//               if (angular.isDefined(changes.ttTextAlign) && ttDirectivesService.isValidAlign(changes.ttTextAlign.currentValue)) {
//                   vm.style.input.textAlign = changes.ttTextAlign.currentValue;
//               }

//               if (angular.isDefined(changes.ttReadonly)) {
//                   vm.readonly = us.toBoolean(changes.ttReadonly.currentValue);
//               }

//               if (angular.isDefined(changes.ttTabable)) {
//                   if (us.toBoolean(changes.ttTabable.currentValue) === false || changes.ttTabable.currentValue === '-1') {
//                       vm.tabable = '-1';
//                   } else {
//                       vm.tabable = '';
//                   }
//               }

//               if (angular.isDefined(changes.ttPlaceholder) && us.isStringValue(changes.ttPlaceholder.currentValue, true)) {
//                   if (vm.ttTranslate === 'false') {
//                       vm.translations.ttPlaceholder = changes.ttPlaceholder.currentValue;
//                   } else {
//                       translateService.translate(changes.ttPlaceholder.currentValue).then((translation) => vm.translations.ttPlaceholder = translation);
//                   }
//               }

//               if (angular.isDefined(changes.ttLabel) && us.isStringValue(changes.ttLabel.currentValue) && changes.ttLabel.currentValue !== changes.ttLabel.previousValue) {
//                   if (vm.ttTranslate === 'false') {
//                       vm.translations.ttLabel = changes.ttLabel.currentValue;
//                   } else {
//                       translateService.translate(changes.ttLabel.currentValue).then(function (translation) {
//                           vm.translations.ttLabel = translation;

//                           vm.whenReady();
//                       });
//                   }
//               }

//               if (angular.isDefined(changes.ttRequired) && angular.isDefined(changes.ttRequired.currentValue)) {
//                   vm.required = us.toBoolean(changes.ttRequired.currentValue);
//               }

//               if (angular.isDefined(changes.ttStyle)) {
//                   setStyle(changes.ttStyle.currentValue);
//               }

//               if (angular.isDefined(changes.ttInputMode) && isValidInputMode(changes.ttInputMode.currentValue) === true) {
//                   vm.inputMode = changes.ttInputMode.currentValue;
//                   setInputMode(vm.inputMode);
//               }

//               if (angular.isDefined(changes.ttMaxLength)) {
//                   vm.maxLength = changes.ttMaxLength.currentValue;
//               }
//           };

//           layoutService.onLayoutChanged(onDestroy, function (info) {
//               if (angular.isUndefined(info)) return;

//               ttDirectivesService.setLayoutStyle(vm.style, info);
//               setStyle(vm.ttStyle);
//               setClasses(info.labelAlwaysOnTop || vm.style.labelAlwaysOnTop);
//           });

//           onDestroy.push(eventService.on('event:focuslock:start', function (data) {
//               if (focusIsBound !== true)
//                   return;

//               bindFocus();
//           }));

//           onDestroy.push(eventService.on('event:focuslock:stop', function (data) {
//               if (focusIsBound !== true)
//                   return;

//               unbindFocus();
//           }));

//           vm.onModelChanged = function (value) {
//               if (angular.isFunction(vm.ttChange)) {
//                   vm.ttChange({ $value: value, $modelId: us.getModelId($element), $args: vm.ttChangeArgs });
//               }
//           };

//           vm.onBlur = function ($event) {
//               if (angular.isFunction(vm.ttOnBlur)) {
//                   vm.ttOnBlur($event);
//               }
//           }

//           vm.$onDestroy = () => ttDirectivesService.onDestroy(onDestroy);
//       }]
//   });
// })();
